import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Performer } from '../performer-maintenance.component';
import { ReferralGroup } from '../../../../shared/constants/referral-groups.constants';

export interface PracticeSoftware {
  id: string;
  name: string;
  email?: string;
}

export interface PerformerDialogData {
  performer?: Performer | null;
  referralGroups: ReferralGroup[];
  isEdit: boolean;
  isRoleBasedUser: boolean;
  userReferralGroupId: number | null;
  userReferralGroupName?: string | null;
}

@Component({
    selector: 'app-performer-add-dialog',
    templateUrl: './performer-add-dialog.component.html',
    styleUrls: ['./performer-add-dialog.component.scss'],
    standalone: false
})
export class PerformerAddDialogComponent implements OnInit {
  performerForm: UntypedFormGroup;
  isSubmitting = false;
  selectedReferralGroupName: string = '';

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<PerformerAddDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PerformerDialogData
  ) {
    this.performerForm = this.createForm();
  }

  ngOnInit(): void {
    if (this.data.performer && this.data.isEdit) {
      this.populateForm();
    }
    if (this.data.isRoleBasedUser && this.data.userReferralGroupId) {
      this.performerForm.patchValue({
        referralGroup: this.data.userReferralGroupId
      });

      if (this.data.isEdit) {
        this.performerForm.get('referralGroup')?.disable();
      }
    }

    this.performerForm.get('referralGroup')?.valueChanges.subscribe(value => {
      const selected = this.data.referralGroups.find(g => g.value === value);
      this.selectedReferralGroupName = selected ? selected.label : '';
    });

    // Set initial referral group name if available
    if (this.data.userReferralGroupName) {
      this.selectedReferralGroupName = this.data.userReferralGroupName;
    }
  }

  createForm(): UntypedFormGroup {
    if (this.data.isEdit) {
      return this.fb.group({
        name: [''],
        referralGroup: [''],
        email: [''],
        agreeTerms: [true],
        agreeReferrals: [true]
      });
    } else {
      return this.fb.group({
        name: ['', [Validators.required]],
        referralGroup: ['', [Validators.required]],
        email: ['', [this.emailsValidator]],
        agreeTerms: [false, Validators.requiredTrue],
        agreeReferrals: [false, Validators.requiredTrue],
      });
    }
  }

  populateForm(): void {
    if (!this.data.performer) return;

    const performer = this.data.performer;

    this.performerForm.patchValue({
      name: this.cleanClinicName(performer.name),
      referralGroup: this.data.userReferralGroupId,
    });
  }

  cleanClinicName(name: string): string {
    if (!name) return '';
    return name.replace(/\t/g, ' ').replace(/\s+/g, ' ').trim();
  }

  emailsValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emails = control.value.split(',').map((email: string) => email.trim());

    for (const email of emails) {
      if (email && !emailRegex.test(email)) {
        return { invalidEmails: true };
      }
    }

    return null;
  }

  isFormValid(): boolean {
    if (this.data.isEdit) {
      const name = this.performerForm.get('name');
      const email = this.performerForm.get('email');

      const nameValid = name?.valid;
      const emailValid = email?.valid;

      return !!(nameValid && emailValid);
    } else {
      return this.performerForm.valid;
    }
  }

  onSave(): void {
    if (this.isFormValid()) {
      const formValue = this.performerForm.getRawValue(); // getRawValue to get disabled fields too

      const performerData = {
        name: formValue.name,
        referralGroup: formValue.referralGroup?.toString() || '',
        email: formValue.email || ''
      };

      if (this.data.isEdit && this.data.performer) {
        const updateData = {
          ...performerData,
          id: this.data.performer.id,
          name: formValue.name,
          referral_group_id: formValue.referralGroup?.toString(),
        };
        this.dialogRef.close(updateData);
      } else {
        this.dialogRef.close(performerData);
      }
    } else {
      this.markFormGroupTouched();
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  markFormGroupTouched(): void {
    Object.keys(this.performerForm.controls).forEach(key => {
      const control = this.performerForm.get(key);
      control?.markAsTouched();
    });
  }

  getErrorMessage(field: string): string {
    const control = this.performerForm.get(field);

    if (control?.hasError('required')) {
      return `${this.getFieldLabel(field)} is required`;
    }

    if (field === 'emails' && control?.hasError('invalidEmails')) {
      return 'Please enter valid email addresses separated by commas';
    }

    return '';
  }

  getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      name: 'Performer Name',
      referralGroup: 'Referral Group',
      email: 'Email'
    };

    return labels[fieldName] || fieldName;
  }

  get dialogTitle(): string {
    return this.data.isEdit ? 'Edit Performer' : 'Add New Performer';
  }

  get saveButtonText(): string {
    return this.data.isEdit ? 'Update Performer' : 'Add Performer';
  }
}